// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import { IconButton, Paper, Tooltip } from '@material-ui/core';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

// Internal Dependencies
import MText from '../../components/Common/MText';
import { containerHeaderTextStyle } from '../../styles/common';
import PageImagesContainer from './Page/PageImagesContainer';
import { IsAdminOrContributor } from '../../common/checks';
import Colours from '../../styles/colours';
import PageLogo from './Page/PageLogo';
import Icons from '../../components/Common/Icons';
import TagsInput from '../../components/Common/TagsInput';
import ApiService from '../../services/apiService';
import pageAction from '../../actions/page';
import MButton from '../../components/MButton';
import Loading from '../../components/Loading';
import { Sleep } from '../../common/util';

const TEXT_SIZE = 13;

const Settings = (props) => {
  const [loading, setLoading] = useState(true);
  const [logo, setLogo] = useState('');
  const [photos, setPhotos] = useState([]);
  const [pagePlan, setPagePlan] = useState('');

  const disabled = !IsAdminOrContributor(props.selectedPage.staffType);

  const onSubmit = async () => {
    try {
      const payload = {
        userTags: props.page.userTags,
      };

      setLoading(true);
      await Sleep(300);

      await ApiService.updatePage(props.selectedPage.id, payload);
      toast.success('Settings updated');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPage = async () => {
    try {
      setLoading(true);

      let page = await ApiService.getPageById(props.selectedPage.id);
      page = { ...page, plan: props.selectedPage.plan };
      setPhotos(page.images || []);
      setPagePlan(page.plan || '');
      setLogo(page.logo || '');

      props.dispatchPageUpdate(page);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
  }, []);

  return (
    <>
      {loading && <Loading />}
      <div
        className="container-fluid"
      >
        <div className="mt-4 ml-2 mb-2 d-flex flex-row justify-content-start align-items-center">
          <MText size={containerHeaderTextStyle.fontSize} bold text="Settings" />
        </div>
        <div className="row">
          <div className="col-12 col-md-10 col-lg-8 my-3">
            <Paper className="p-4">
              <div className="d-flex flex-column">
                <div>
                  <PageImagesContainer
                    mediaType="image"
                    images={photos}
                    windowSize={props.windowSize}
                    pageId={props.selectedPage.id}
                    accountType={pagePlan}
                    disabled={disabled}
                  />
                </div>
              </div>
              <div className="col-md-12">
                <span className="d-block mb-3" style={{ width: '100%', borderTop: `1px solid ${Colours.Gray4}` }} />
              </div>
              <div className="mb-4">
                <MText
                  variant="body1"
                  className="mb-2"
                  colour={Colours.Gray}
                  size={TEXT_SIZE}
                  text="2. Logo (min. 200 x 200px resolution)"
                />
                <PageLogo
                  setLogo={(url) => setLogo(url)}
                  currentPhoto={logo}
                  pageId={props.selectedPage.id}
                  disabled={disabled}
                />
              </div>

              <div className="mb-4">
                <div className="d-flex align-items-center mb-2" style={{ gap: 4 }}>
                  <MText
                    variant="body1"
                    colour={Colours.Gray}
                    size={TEXT_SIZE}
                    text="3. Tags"
                  />
                  <Tooltip title="Associate tags with your page">
                    <IconButton size="small">
                      <Icons.InfoOutlined colour={Colours.Gray1} fontSize="1.3rem" />
                    </IconButton>
                  </Tooltip>
                </div>

                <TagsInput
                  data={props.page.userTags}
                  onChange={(values) => props.dispatchPageUpdate({ userTags: values })}
                  disabled={disabled}
                />
              </div>

              <div className="pb-4 d-flex flex-row justify-content-center">
                <div className="col-12 col-sm-8 col-lg-6 px-0">
                  <Tooltip title={!disabled ? '' : 'Permission Denied'}>
                    <MButton
                      className="block py-2"
                      variant="contained"
                      colour={disabled ? Colours.Gray4 : Colours.Secondary}
                      disabled={disabled}
                      text="Save"
                      textMedium
                      textSize={14}
                      textColour={Colours.White}
                      onClick={onSubmit}
                    />
                  </Tooltip>
                </div>
              </div>
            </Paper>
          </div>
        </div>
      </div>
    </>
  );
};

Settings.propTypes = {
  page: PropTypes.shape({
    userTags: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  selectedPage: PropTypes.shape({
    id:        PropTypes.number,
    staffType: PropTypes.string,
    plan:      PropTypes.string,
  }).isRequired,
  windowSize:         PropTypes.string.isRequired,
  dispatchPageUpdate: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  page:         state.page.update,
  selectedPage: state.page.selected,
  windowSize:   state.layout.windowSize,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchPageUpdate: (page) => pageAction.pageUpdate(dispatch, page),
});

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
